import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import { RideStatusBadge, userDisplayName, useActiveOrg } from "common-fe";
// #1805 — c.scheduledTime / r.scheduledTime are FVD Access stamps (#1811),
// not genuine instants, so both formatTime and formatRideTime below render
// through the shared FVD_STAMP_TIMEZONE constant, not a local
// "America/Los_Angeles" literal or the show's real venueTimezone.
import { FVD_STAMP_TIMEZONE } from "common-fe/venueTime";
import { useTranslation } from "i18n-fe";
import {
  ERROR_DEDUCTION_SCHEMES,
  DSHB_CLASS_KINDS,
  RESULT_LAYOUTS,
  resolveResultLayout,
  resolveErrorTiers,
  errorTierForOccurrence,
} from "score-common";
import { classNumberLabel } from "../util/classDisplay";
import { hasAssignScribesPermission } from "../permissions";
import { useScoreApi, unwrap } from "../api/scoreApi";
import useRideStatuses from "../hooks/useRideStatuses";
import useScorePageLink from "../hooks/useScorePageLink";
import RideLink from "./RideLink";

// #772 (workstream error-deductions, #771-774) — human-facing labels for the
// canonical ERROR_DEDUCTION_SCHEMES enum (score-common). The enum value is
// what's persisted; these i18n-mapped labels are display-only — never
// persist the label text itself.
const ERROR_DEDUCTION_SCHEME_LABEL_KEYS = {
  [ERROR_DEDUCTION_SCHEMES.POINTS]: "ring.errorDeductionSchemePoints",
  [ERROR_DEDUCTION_SCHEMES.PERCENT_SENIOR]: "ring.errorDeductionSchemePercentSenior",
  [ERROR_DEDUCTION_SCHEMES.PERCENT_YOUTH]: "ring.errorDeductionSchemePercentYouth",
};

// #1250: a class with NO persisted errorDeductionScheme was previously
// displayed as if "Points — 2 / 4 / elim" were selected (`value={c.
// errorDeductionScheme ?? ERROR_DEDUCTION_SCHEMES.POINTS}`), even though
// nothing had actually been saved and the class was really scoring off the
// catalog test's own rule (which for freestyle is now the #1250 flat -2,
// never POINTS' escalating 2/4/elim). That made the Select lie: what it
// showed was not what scored. This sentinel is a display-only placeholder,
// never a persisted value. It renders in the disabled slot so the closed
// Select shows it accurately, but it is not a selectable MenuItem, so there
// is no path that ever sends it to onSaveErrorDeductionScheme.
//
// Deliberately NOT an empty string: MUI's Select treats `value === ""` as
// "empty" for its filled/shrink state regardless of `displayEmpty` (that
// prop only controls whether placeholder TEXT renders, not the label's
// shrink), so an empty-string value left the floating "Error deduction"
// InputLabel un-shrunk, overlapping the placeholder text in the closed
// Select (caught by the real-Chrome light/dark screenshot this card's UI
// change required, not by Cypress/Electron). A non-empty sentinel that can
// never collide with a real ERROR_DEDUCTION_SCHEMES value makes MUI treat
// it as a normal filled selection, so the label shrinks correctly.
const SCHEME_NOT_SET = "NOT_SET";

// #2277 — human-facing labels for the DSHB_CLASS_KINDS enum, used by the
// override Select below. Same precedent as ERROR_DEDUCTION_SCHEME_LABEL_KEYS
// above: the enum value is what's persisted, these are display-only.
//
// #2729 (judge finding, second pass) — exported so the same completeness
// pin that covers BulkAssignScribesDialog's separate copy of this map
// covers this one too. Both are rendered by
// `Object.values(DSHB_CLASS_KINDS).map((kind) => t(MAP[kind]))`, so a member
// missing from either map renders a blank, still-selectable MenuItem
// (i18next's t(undefined) === "") that writes that enum value on pick. This
// copy is complete today; the guard is what stops the NEXT enum member from
// reintroducing the bug in only one of the two places.
export const DSHB_KIND_OVERRIDE_LABEL_KEYS = {
  [DSHB_CLASS_KINDS.CHAMPIONSHIP]: "ring.dshbKindOverrideChampionship",
  [DSHB_CLASS_KINDS.USDFBC_CHAMPIONSHIP]: "ring.dshbKindOverrideUsdfbcChampionship",
  [DSHB_CLASS_KINDS.MATERIALE]: "ring.dshbKindOverrideMateriale",
  [DSHB_CLASS_KINDS.UNDER_SADDLE]: "ring.dshbKindOverrideUnderSaddle",
  [DSHB_CLASS_KINDS.SUITABILITY]: "ring.dshbKindOverrideSuitability",
  // #2729 - the escape hatch: turns the className/fvdTestId guess off
  // entirely for this class.
  [DSHB_CLASS_KINDS.NOT_DSHB]: "ring.dshbKindOverrideNotDshb",
};

// #2277 — sentinel for "no override stored, use the className/fvdTestId
// guess" (card #2277's classifyDshbClassKind dshbKindOverride param treats
// null/absent as unset). Selecting this MenuItem sends `null` to
// onSaveDshbKindOverride, clearing the stored override - unlike
// SCHEME_NOT_SET above, this one IS selectable, since clearing back to the
// guess is exactly what AC2 requires. Non-empty per SCHEME_NOT_SET's own
// comment on why MUI's Select needs a non-"" sentinel for the shrunk label.
const DSHB_KIND_OVERRIDE_AUTO = "AUTO";

// #2567 — sentinel for "no sponsor selected", same reason and same fix as
// SCHEME_NOT_SET/DSHB_KIND_OVERRIDE_AUTO above: MUI's Select treats
// `value === ""` as empty for its label shrink state regardless of
// `displayEmpty`, so an empty-string sponsorIdInput left the floating
// "Sponsor" InputLabel un-shrunk, overlapping the "no sponsor" placeholder
// text in the closed Select (caught by this card's real-Chrome light-mode
// screenshot, not by Cypress/Electron). Selecting this MenuItem still
// commits `sponsorId: null` (commitSponsorship treats it the same as "").
const SPONSOR_NONE = "NONE";

// #2369: ordinal labels for describeErrorSchedule below. Every real tier
// array in the catalog (buildErrorTiers/SCHEME_TIERS/FREESTYLE_ERROR_TIERS,
// packages/score-common/src/utils/scoring.js) is 1-3 entries long; a 4th
// slot is defensive only, no def or scheme currently reaches it.
const ERROR_SCHEDULE_ORDINAL_KEYS = [
  "ring.errorScheduleOrdinal1st",
  "ring.errorScheduleOrdinal2nd",
  "ring.errorScheduleOrdinal3rd",
  "ring.errorScheduleOrdinal4th",
];

// #2369: one tier's amount, human-facing. Mirrors the three tier shapes
// resolveErrorTiers ever returns (score-common/utils/scoring.js:
// tierFromRuleValue, SCHEME_TIERS, FREESTYLE_ERROR_TIERS): eliminate,
// percent, or points. No other shape exists, so an unrecognized tier
// renders as "".
function tierAmountLabel(tier, t) {
  if (!tier) return "";
  if (tier.eliminate) return t("ring.errorScheduleEliminate");
  if (tier.percent != null) return t("ring.errorScheduleAmountPercent", { amount: tier.percent });
  if (tier.points != null) return t("ring.errorScheduleAmountPoints", { amount: tier.points });
  return "";
}

// #2369: render the ORDERED tiers resolveErrorTiers returns as the text an
// admin reads to know what will actually be deducted. Deliberately reads
// tiers via errorTierForOccurrence (the same function scribe-fe's
// recordError calls to resolve a live occurrence) rather than mapping the
// array directly, so this never drifts from how an occurrence is actually
// scored. An empty array (errorRule.unit === "none", #1300, no
// error-of-course concept for this test at all) is its own case, not a
// zero-tier schedule.
function describeErrorSchedule(tiers, t) {
  if (!Array.isArray(tiers) || tiers.length === 0) {
    return t("ring.errorScheduleNoConcept");
  }
  const parts = tiers.map((_, idx) => {
    const ordinalKey =
      ERROR_SCHEDULE_ORDINAL_KEYS[idx] ??
      ERROR_SCHEDULE_ORDINAL_KEYS[ERROR_SCHEDULE_ORDINAL_KEYS.length - 1];
    const tier = errorTierForOccurrence(tiers, idx + 1);
    return `${t(ordinalKey)} ${tierAmountLabel(tier, t)}`;
  });
  // The last tier repeats forever for any further occurrence (errorTierFor
  // Occurrence clamps past the array's end) unless it already eliminates,
  // in which case there is no "further occurrence" to describe.
  const last = tiers[tiers.length - 1];
  if (!last?.eliminate) {
    parts[parts.length - 1] += ` (${t("ring.errorScheduleRepeats")})`;
  }
  return parts.join(", ");
}

// #2422 — the Accordion summary line: names only the settings that
// actually deviate from "nothing set" (test defaults, auto-detected DSHB
// kind, no paper scoring, no walk/trot restriction, no T/Q topology, no
// section judges assigned), so a show secretary can tell whether the
// config wall holds anything worth opening without opening it.
// EQUITATION_SECTION_CODE is defined just below this function, so this
// reads it by reference at call time, not at parse time.
function summarizeClassConfig(c, t) {
  const parts = [];
  if (c.errorDeductionScheme) {
    parts.push(t(ERROR_DEDUCTION_SCHEME_LABEL_KEYS[c.errorDeductionScheme]));
  }
  if (c.faultTimeLimitSeconds != null) {
    parts.push(t("ring.classConfigSummaryFaultTime", { seconds: c.faultTimeLimitSeconds }));
  }
  // Deliberate: mirrors the walkTrotOnly control's own visibility gate
  // (only equitation classes show/use the Switch), so this never reports a
  // value the operator has no way to see or change for this class.
  if (c.sectionCode === EQUITATION_SECTION_CODE && c.walkTrotOnly === true) {
    parts.push(t("ring.walkTrotOnly"));
  }
  if (c.paperEntry === true) {
    parts.push(t("ring.classConfigSummaryPaperEntry"));
  }
  if (c.dshbClassKindOverride) {
    // #2729 (repair item) - a stored value with no entry in the label map
    // (any value not in the DSHB_CLASS_KINDS enum this build knows about)
    // must never call t() with an undefined key. Falls back to the raw
    // stored string instead, which is exactly the state the 13 prod rows
    // written before NOT_DSHB was a real enum member were in.
    const labelKey = DSHB_KIND_OVERRIDE_LABEL_KEYS[c.dshbClassKindOverride];
    parts.push(
      labelKey
        ? t(labelKey)
        : t("ring.dshbKindOverrideUnrecognized", { value: c.dshbClassKindOverride })
    );
  }
  // #2421 (repair item 8) — #2422's standing obligation applies here too:
  // this dialog is the first place in scoreadmin that can SET resultLayout
  // (the drawer itself has no control for it), so without this branch a
  // class bulk-set to COMBINED reads "nothing overridden" here — the exact
  // defect #2422 had to repair mid-flight for the other fields.
  if (resolveResultLayout(c.resultLayout) === RESULT_LAYOUTS.COMBINED) {
    parts.push(t("ring.classConfigSummaryResultLayout"));
  }
  if (c.qualityJudging?.topology) {
    const topologyLabel =
      c.qualityJudging.topology === "two-judge" ? t("tq.twoJudge") : t("tq.technicalOnly");
    parts.push(t("ring.classConfigSummaryQualityJudging", { topology: topologyLabel }));
  }
  if (c.sectionJudgeAssignments?.technical || c.sectionJudgeAssignments?.quality) {
    parts.push(t("ring.classConfigSummarySectionJudges"));
  }
  // #2567 — #2422's standing obligation applies here too: sponsorId/prize
  // are set from this drawer's Sponsorship section below, nowhere else, so
  // a sponsored class must not read "nothing overridden" here.
  if (c.sponsorId || c.prize) {
    parts.push(t("ring.classConfigSummarySponsorship"));
  }
  return parts.length ? parts.join(" · ") : t("ring.classConfigSummaryDefault");
}

// #1783: the FVD-native equitation marker (prod check on card #1783: 19/19
// dressage seat equitation classes carry sectionCode "6900", 0/380
// non-equitation classes do). Gates visibility of the walk/trot-only
// Switch below: only equitation classes need the control in practice, and
// hiding it elsewhere keeps hundreds of irrelevant class rows uncluttered.
const EQUITATION_SECTION_CODE = "6900";

// #600 — while a scribe/judging assignment is persisting, the Selects are
// disabled AND show a spinner in place of the dropdown arrow. MUI passes its
// icon `className` (which positions it at the right edge) through to this
// component, so the spinner lands where the arrow was.
function SavingIcon(props) {
  return <CircularProgress size={18} color="inherit" className={props.className} />;
}

function formatTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : // #612 / #1805 — render the FVD stamp through FVD_STAMP_TIMEZONE.
      d.toLocaleString([], {
        weekday: "short",
        hour: "numeric",
        minute: "2-digit",
        timeZone: FVD_STAMP_TIMEZONE,
      });
}

// #612 / #1805 — per-ride clock time, through FVD_STAMP_TIMEZONE (hour:minute only).
function formatRideTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        timeZone: FVD_STAMP_TIMEZONE,
      });
}

// #615 — per-ride list for a class: one row per ride showing time, rider,
// horse and a live status badge. Rides are fetched when the drawer is open;
// status comes from the shared useRideStatuses hook + RideStatusBadge (reused
// from ClassPlacings) so the badge stays consistent across the app.
// #655 — ring moved to the class info header (every ride in a class shares
// the class's ring, so repeating it per row was noise); classRing is kept
// here only to flag the rare bad/legacy-data case where a ride disagrees.
function ClassRideList({ classId, open, classRing }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // #947 — per-row "open rider's score page" link, mirroring RideEditor's
  // button but as a compact icon here (one row per ride). `openingRideId`
  // scopes the spinner/disabled state to the row actually being opened, and
  // `linkErrorRideId` scopes the inline error the same way, so one row's
  // failure never disables/blank the whole table.
  const { getLink: getScorePageLink } = useScorePageLink();
  const [openingRideId, setOpeningRideId] = useState(null);
  const [linkErrorRideId, setLinkErrorRideId] = useState(null);
  const handleOpenScorePage = async (rideId) => {
    setLinkErrorRideId(null);
    setOpeningRideId(rideId);
    try {
      const url = await getScorePageLink(rideId);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setLinkErrorRideId(rideId);
    } finally {
      setOpeningRideId(null);
    }
  };

  useEffect(() => {
    if (!open || !classId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    scoreApi.rides
      .listRides(classId)
      .then(unwrap)
      .then((list) => {
        if (cancelled) return;
        // Sort by scheduled time, falling back to ride number.
        const sorted = [...(list || [])].sort((a, b) => {
          const ta = a.scheduledTime ? Date.parse(a.scheduledTime) : NaN;
          const tb = b.scheduledTime ? Date.parse(b.scheduledTime) : NaN;
          if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) return ta - tb;
          return (a.rideNumber ?? Infinity) - (b.rideNumber ?? Infinity);
        });
        setRides(sorted);
      })
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, classId, scoreApi]);

  const rideIds = useMemo(() => rides.map((r) => r.id).filter(Boolean), [rides]);
  const { statuses } = useRideStatuses(rideIds);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ mb: 2 }}>
        {t("ring.ridesLoadError")}
      </Typography>
    );
  }
  if (!rides.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("ring.noRides")}
      </Typography>
    );
  }

  return (
    <Table size="small" sx={{ mb: 2 }}>
      <TableBody>
        {rides.map((r) => {
          // Legacy/bad data only — rides are FVD-stamped with their parent
          // class's ring at ingest, so this should never legitimately fire.
          // Don't silently hide a disagreement; flag it instead of dropping it.
          const ringMismatch = r.ring && classRing && r.ring !== classRing;
          return (
            <TableRow key={r.id} hover>
              <TableCell sx={{ color: "text.primary", whiteSpace: "nowrap" }}>
                {formatRideTime(r.scheduledTime) || "—"}
              </TableCell>
              <TableCell sx={{ color: "text.primary" }}>
                {/* #860 — shared RideLink ("#CNO, Rider on Horse"), linked
                    into the RideEditor route. Supersedes #737's bare
                    riderName/horseName pair (this table had no CNO before). */}
                <RideLink ride={r} />
                {ringMismatch && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="error.main"
                    sx={{ ml: 1 }}
                  >
                    {t("ring.rideRingMismatch", { ring: r.ring })}
                  </Typography>
                )}
                {linkErrorRideId === r.id && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="error.main"
                    sx={{ ml: 1 }}
                  >
                    {t("ring.scorePageLinkError")}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <RideStatusBadge status={statuses[r.id]} />
                {/* #947 — every class menu's ride row gets a direct link to
                    that rider's score page (silent create-or-reuse, never
                    emails — see useScorePageLink). Hidden with no riderId
                    (nothing for report-api's lookup to key on). */}
                {r.riderId && (
                  <Tooltip title={t("ring.scorePageLink")}>
                    <span>
                      <IconButton
                        size="small"
                        sx={{ ml: 0.5 }}
                        data-testid={`score-page-link-${r.id}`}
                        disabled={openingRideId === r.id}
                        onClick={() => handleOpenScorePage(r.id)}
                        aria-label={t("ring.scorePageLink")}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// #553 — per-class detail drawer. The scribe assignments + Technical/Quality
// judging config that used to sit inline on the classes page now live here, plus
// the class's default (FVD-scheduled) time. Right-anchored slide-out, matching
// RideHistoryDrawer. All the editing handlers are passed in from ClassManager so
// the write/reload logic stays in one place.
export default function ClassDetailDrawer({
  open,
  onClose,
  classItem,
  test,
  scribes,
  saving,
  positionsForClass,
  scribeLabel,
  onAssign,
  isYoungHorse,
  allJudges,
  onSaveQualityJudging,
  onSaveFaultTimeLimit,
  onSaveErrorDeductionScheme,
  onSaveWalkTrotOnly,
  onSaveDshbKindOverride,
  onSavePaperEntry,
  sponsors,
  onSaveSponsorship,
  judges,
  judgeLabel,
  onSaveSectionJudgeAssignments,
}) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const c = classItem;
  const positions = c ? positionsForClass(c) : [];
  const scheduledTime = c ? formatTime(c.scheduledTime || c.time) : null;

  // #2415 — gate the paper-scoring toggle on score:assign_scribes, the same
  // permission score-api's PUT /class/:classId/paper-entry route requires
  // (see apps/scoreadmin-fe/src/permissions.js for why this reads the
  // token's roles claim directly rather than common-fe's usePermission()).
  const auth = useAuth();
  const { activeOrgId } = useActiveOrg();
  const canAssignScribes = useMemo(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken || !activeOrgId) return false;
    try {
      const claims = jwtDecode(accessToken);
      const raw = claims.roles;
      const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
      return hasAssignScribesPermission(map[activeOrgId]);
    } catch {
      return false;
    }
  }, [auth.user?.access_token, activeOrgId]);

  // #2415 — "existing sheets are untouched" warning (card's Watch out:
  // paperEntry is stamped onto a ScoreSheet only at CREATION, never
  // retroactively — apps/score-api/src/handlers/scoresheet.js's
  // isPaperEntryClass/createScoreSheet). Lazily checks whether the class's
  // rides already carry any ScoreSheet, independent of ClassRideList's own
  // rides fetch below (that one is scoped to the Rides section and doesn't
  // expose its state to this level).
  const [hasExistingScoreSheets, setHasExistingScoreSheets] = useState(false);
  useEffect(() => {
    if (!open || !c?.id) {
      setHasExistingScoreSheets(false);
      return undefined;
    }
    let cancelled = false;
    scoreApi.rides
      .listRides(c.id)
      .then(unwrap)
      .then((rides) => {
        const rideIds = (rides || []).map((r) => r.id).filter(Boolean);
        if (!rideIds.length) return [];
        return scoreApi.scoreSheets.listScoreSheetsForRides({ rideIds }).then(unwrap);
      })
      .then((sheets) => {
        if (!cancelled) setHasExistingScoreSheets(Array.isArray(sheets) && sheets.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasExistingScoreSheets(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, c?.id, scoreApi]);

  // #2369: resolve the SAME schedule the scoring engine actually applies
  // (resolveErrorTiers, score-common/utils/scoring.js), instead of
  // re-implementing its class-override / freestyle-default / catalog-def
  // precedence here. A second copy of that precedence in the FE would drift
  // from the engine, and drift on an eliminating tier is a wrong
  // elimination. See resolveErrorTiers' own docstring and card #2369.
  const effectiveErrorTiers = c
    ? resolveErrorTiers({ errorDeductionScheme: c.errorDeductionScheme, test })
    : null;
  const effectiveErrorScheduleSourceKey = c?.errorDeductionScheme
    ? "ring.errorScheduleSourceClass"
    : "ring.errorScheduleSourceTestDefault";

  // #570 — Fault time limit is a plain numeric TextField, so (unlike the
  // qualityJudging Selects, which fire onSave on every discrete selection)
  // it needs local state to be typeable; onBlur is the input's equivalent
  // of a Select's onChange "commit" and matches the same no-separate-save-
  // button pattern (persist immediately, no icon button).
  const [faultTimeLimitInput, setFaultTimeLimitInput] = useState("");
  useEffect(() => {
    setFaultTimeLimitInput(
      c?.faultTimeLimitSeconds != null ? String(c.faultTimeLimitSeconds) : "",
    );
  }, [c?.id, c?.faultTimeLimitSeconds]);

  // #2567 — sponsorId + prize buffered locally, same reason as
  // faultTimeLimitInput above (the prize TextField needs to be typeable,
  // committing onBlur). Both fields are kept here together, NOT two
  // independent buffers, because score-api's setClassSponsorship is a FULL
  // REPLACE (`const { sponsorId = null, prize } = request.body` — see
  // class-config.js): sending only the field the operator touched would
  // silently clear the other (card #2565's comment on this exact trap).
  // commitSponsorship always sends both current buffered values, whichever
  // one changed.
  const [sponsorIdInput, setSponsorIdInput] = useState(SPONSOR_NONE);
  const [prizeInput, setPrizeInput] = useState("");
  useEffect(() => {
    setSponsorIdInput(c?.sponsorId || SPONSOR_NONE);
    setPrizeInput(c?.prize ?? "");
  }, [c?.id, c?.sponsorId, c?.prize]);
  const commitSponsorship = (nextSponsorId, nextPrize) => {
    const trimmedPrize = typeof nextPrize === "string" ? nextPrize.trim() : "";
    onSaveSponsorship(c.id, {
      sponsorId: nextSponsorId && nextSponsorId !== SPONSOR_NONE ? nextSponsorId : null,
      prize: trimmedPrize || null,
    });
  };
  const handleSponsorChange = (e) => {
    const value = e.target.value;
    setSponsorIdInput(value);
    commitSponsorship(value, prizeInput);
  };
  const handlePrizeBlur = () => {
    if ((c?.prize ?? "") === prizeInput) return;
    commitSponsorship(sponsorIdInput, prizeInput);
  };
  // #2593 (known gap, tracked separately) — deleting a sponsor never clears
  // sponsorId off class rows it's referenced from, so a class can hold a
  // sponsorId absent from the current org sponsor list. Same stale-
  // reference-in-tooltip treatment as the scribe Select above (#865): the
  // dangling id stays selected (never silently swapped for a different
  // sponsor, never crashes), its raw value shown in a tooltip.
  const sponsorList = sponsors || [];
  const sponsorMissing =
    sponsorIdInput !== SPONSOR_NONE && !sponsorList.some((s) => s.id === sponsorIdInput);

  // The drawer sits above the app bar (zIndex.modal + 100). A Select's popover
  // menu is itself a modal (default zIndex.modal), so without this it renders
  // BEHIND the drawer and its options can't be clicked — lift the menus above.
  const menuProps = { sx: { zIndex: (theme) => theme.zIndex.modal + 200 } };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
    >
      <Box sx={{ width: { xs: "100vw", sm: "75vw" }, maxWidth: 1100, p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5" color="text.primary">
            {/* #673 — classNumberLabel() already returns "<number>. <name>"; a
                separate line repeating className here duplicated the name. */}
            {c ? classNumberLabel(c) : ""}
          </Typography>
          <IconButton onClick={onClose} aria-label={t("ring.closeDetail")}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scheduled (FVD-scheduled) time + ring — read-only; FVD owns both.
            #655 — ring used to repeat on every ride row (all rides in a class
            share one ring); it lives here once, next to the scheduled time. */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>{t("ring.scheduledTime")}:</strong> {scheduledTime || t("ring.noTime")}
          {" · "}
          {c?.ring ? t("ring.ringLabel", { ring: c.ring }) : t("ring.unassignedRing")}
        </Typography>

        {/* #2422 — the class config controls below (previously a flat,
            ungrouped stack of Selects/Switches) live inside a collapsed-by-
            default Accordion: most drawer opens are for something else
            (scribing, rides), and the config wall doesn't need to be in the
            way. The summary line names whatever is actually set so a show
            secretary can tell whether to open it without opening it. */}
        {c && (
          <Accordion
            disableGutters
            sx={{ mb: 2, "&:before": { display: "none" } }}
            data-testid={`class-config-accordion-${c.id}`}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              data-testid={`class-config-accordion-summary-${c.id}`}
              aria-controls={`class-config-accordion-content-${c.id}`}
              id={`class-config-accordion-header-${c.id}`}
            >
              <Box>
                <Typography variant="subtitle1" color="text.primary">
                  {t("ring.classConfigHeading")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {summarizeClassConfig(c, t)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails id={`class-config-accordion-content-${c.id}`}>
              <Typography variant="overline" color="text.secondary">
                {t("ring.classConfigJudgingHeading")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1, mb: 2 }}>
                {/* #570 — admin-editable per-class fault time limit (seconds).
                    USEF's actual rule for exceeding this is elimination (a
                    judge call), never an auto-applied deduction — this field
                    only feeds the "exceeded Xs" flag surfaced elsewhere; it
                    doesn't itself apply any penalty. */}
                <TextField
                  type="number"
                  size="small"
                  label={t("ring.faultTimeLimit")}
                  helperText={t("ring.faultTimeLimitHelp")}
                  value={faultTimeLimitInput}
                  disabled={saving}
                  sx={{ width: 220 }}
                  inputProps={{ min: 0, "data-testid": `fault-time-limit-${c.id}` }}
                  onChange={(e) => setFaultTimeLimitInput(e.target.value)}
                  onBlur={() => {
                    const parsed = parseInt(faultTimeLimitInput, 10);
                    if (Number.isNaN(parsed) || parsed === c.faultTimeLimitSeconds) return;
                    onSaveFaultTimeLimit(c.id, parsed);
                  }}
                />

                {/* #772 (workstream error-deductions, #771-774) — per-class
                    error-of-course/test deduction scheme. Persisted value is
                    the canonical enum (ERROR_DEDUCTION_SCHEMES, score-common);
                    #771's engine and #773's scribe apply resolve off the same
                    values. */}
                <FormControl
                  size="small"
                  sx={{ width: 280 }}
                  data-testid={`error-deduction-scheme-${c.id}`}
                >
                  <InputLabel id={`${c.id}-error-deduction-scheme-label`}>
                    {t("ring.errorDeductionScheme")}
                  </InputLabel>
                  <Select
                    labelId={`${c.id}-error-deduction-scheme-label`}
                    label={t("ring.errorDeductionScheme")}
                    value={c.errorDeductionScheme ?? SCHEME_NOT_SET}
                    disabled={saving}
                    MenuProps={menuProps}
                    IconComponent={saving ? SavingIcon : undefined}
                    onChange={(e) => onSaveErrorDeductionScheme(c.id, e.target.value)}
                  >
                    {/* #1250: display-only, represents "nothing persisted yet,
                        the test's own rule applies". `disabled` so it can't be
                        picked from the open menu, only ever rendered as the
                        closed Select's current value. */}
                    <MenuItem value={SCHEME_NOT_SET} disabled>
                      {t("ring.errorDeductionSchemeNotSet")}
                    </MenuItem>
                    {Object.values(ERROR_DEDUCTION_SCHEMES).map((scheme) => (
                      <MenuItem key={scheme} value={scheme}>
                        {t(ERROR_DEDUCTION_SCHEME_LABEL_KEYS[scheme])}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{t("ring.errorDeductionSchemeHelp")}</FormHelperText>
                </FormControl>
              </Box>

              {/* #2369: the resolved answer, visible. The Select above shows
                  what was PICKED (or the "Not set" placeholder); this shows
                  what will actually be applied, naming its source (an
                  explicit class override vs the test's own default) so an
                  unset value stops being an invisible default. See card #2369
                  and resolveErrorTiers in
                  packages/score-common/src/utils/scoring.js.
                  Gated on (!c.testId || test): while a class HAS a testId but
                  testsById hasn't loaded yet, `test` is transiently undefined
                  and resolveErrorTiers would fall back to the generic
                  DEFAULT_ERROR_TIERS, showing a schedule that is not actually
                  the one that will apply. Hide the line rather than show a
                  wrong answer for that one render. */}
              {(!c.testId || test || c.errorDeductionScheme) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, mt: -1 }}
                  data-testid={`error-deduction-effective-${c.id}`}
                >
                  {t("ring.errorScheduleLabel")} ({t(effectiveErrorScheduleSourceKey)}):{" "}
                  {describeErrorSchedule(effectiveErrorTiers, t)}
                </Typography>
              )}

              {/* #542 — Technical/Quality judging, only on young-horse /
                  Children T/Q. */}
              {isYoungHorse(c) && (
                <Box data-testid={`tq-config-${c.id}`} sx={{ mb: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    {t("tq.heading")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("tq.help")}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <FormControl size="small" sx={{ width: 280 }}>
                      <InputLabel id={`${c.id}-tq-topology-label`}>{t("tq.topology")}</InputLabel>
                      <Select
                        labelId={`${c.id}-tq-topology-label`}
                        label={t("tq.topology")}
                        value={c.qualityJudging?.topology ?? ""}
                        disabled={saving}
                        MenuProps={menuProps}
                        IconComponent={saving ? SavingIcon : undefined}
                        inputProps={{ "data-testid": `tq-topology-${c.id}` }}
                        onChange={(e) =>
                          onSaveQualityJudging(c.id, {
                            topology: e.target.value,
                            qualityPosition: c.qualityJudging?.qualityPosition,
                            qualityJudge: c.qualityJudging?.qualityJudge,
                          })
                        }
                      >
                        <MenuItem value="">
                          <em>{t("ring.none")}</em>
                        </MenuItem>
                        <MenuItem value="two-judge">{t("tq.twoJudge")}</MenuItem>
                        <MenuItem value="technical-only">{t("tq.technicalOnly")}</MenuItem>
                      </Select>
                      <FormHelperText>{t("tq.topologyHelp")}</FormHelperText>
                    </FormControl>

                    {c.qualityJudging?.topology === "two-judge" && (
                      <>
                        <Typography variant="body2" color="text.primary" sx={{ width: "100%", mb: -1 }}>
                          {t("tq.technicalJudge")}
                        </Typography>
                        <FormControl size="small" sx={{ width: 160 }}>
                          <InputLabel id={`${c.id}-tq-pos-label`}>{t("tq.qualityPosition")}</InputLabel>
                          <Select
                            labelId={`${c.id}-tq-pos-label`}
                            label={t("tq.qualityPosition")}
                            value={c.qualityJudging?.qualityPosition ?? ""}
                            disabled={saving}
                            MenuProps={menuProps}
                            IconComponent={saving ? SavingIcon : undefined}
                            inputProps={{ "data-testid": `tq-position-${c.id}` }}
                            onChange={(e) =>
                              onSaveQualityJudging(c.id, {
                                topology: "two-judge",
                                qualityPosition: e.target.value || null,
                                qualityJudge: c.qualityJudging?.qualityJudge,
                              })
                            }
                          >
                            <MenuItem value="">
                              <em>{t("ring.none")}</em>
                            </MenuItem>
                            <MenuItem value="E">E</MenuItem>
                            <MenuItem value="B">B</MenuItem>
                          </Select>
                          <FormHelperText>{t("tq.qualityPositionHelp")}</FormHelperText>
                        </FormControl>
                        <FormControl size="small" sx={{ width: 280 }}>
                          <InputLabel id={`${c.id}-tq-judge-label`}>{t("tq.qualityJudge")}</InputLabel>
                          <Select
                            labelId={`${c.id}-tq-judge-label`}
                            label={t("tq.qualityJudge")}
                            value={c.qualityJudging?.qualityJudge ?? ""}
                            disabled={saving || allJudges.length === 0}
                            MenuProps={menuProps}
                            IconComponent={saving ? SavingIcon : undefined}
                            inputProps={{ "data-testid": `tq-judge-${c.id}` }}
                            onChange={(e) =>
                              onSaveQualityJudging(c.id, {
                                topology: "two-judge",
                                qualityPosition: c.qualityJudging?.qualityPosition,
                                qualityJudge: e.target.value || null,
                              })
                            }
                          >
                            <MenuItem value="">
                              <em>{t("tq.selectJudge")}</em>
                            </MenuItem>
                            {c.qualityJudging?.qualityJudge &&
                              !allJudges.includes(c.qualityJudging.qualityJudge) && (
                                <MenuItem value={c.qualityJudging.qualityJudge}>
                                  {c.qualityJudging.qualityJudge}
                                </MenuItem>
                              )}
                            {allJudges.map((j) => (
                              <MenuItem key={j} value={j}>
                                {j}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{t("tq.qualityJudgeHelp")}</FormHelperText>
                        </FormControl>
                      </>
                    )}
                  </Box>

                  {/* #760 — per-section (technical/quality) judge assignment,
                      from #759's Class.sectionJudgeAssignments field/API.
                      Distinct from the topology config above: this assigns a
                      score-judge ROSTER id (feeds signatures/roster display,
                      card #730) to each section, independent of which booth
                      position scores it. */}
                  <Box data-testid={`section-judge-config-${c.id}`} sx={{ mt: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      {t("sectionJudge.heading")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t("sectionJudge.help")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {["technical", "quality"].map((section) => {
                        const value = c.sectionJudgeAssignments?.[section] ?? "";
                        const label =
                          section === "technical"
                            ? t("sectionJudge.technical")
                            : t("sectionJudge.quality");
                        return (
                          <FormControl
                            key={section}
                            size="small"
                            sx={{ width: 280 }}
                            data-testid={`section-judge-${section}-${c.id}`}
                          >
                            <InputLabel id={`${c.id}-section-judge-${section}-label`}>
                              {label}
                            </InputLabel>
                            <Select
                              labelId={`${c.id}-section-judge-${section}-label`}
                              label={label}
                              value={value}
                              disabled={saving || judges.length === 0}
                              MenuProps={menuProps}
                              IconComponent={saving ? SavingIcon : undefined}
                              onChange={(e) =>
                                onSaveSectionJudgeAssignments(c.id, {
                                  technical: c.sectionJudgeAssignments?.technical ?? null,
                                  quality: c.sectionJudgeAssignments?.quality ?? null,
                                  [section]: e.target.value || null,
                                })
                              }
                            >
                              <MenuItem value="">
                                <em>
                                  {judges.length === 0
                                    ? t("sectionJudge.noJudges")
                                    : t("sectionJudge.selectJudge")}
                                </em>
                              </MenuItem>
                              {/* #865 — same quiet-placeholder + tooltip
                                  treatment as the scribe Select above. */}
                              {value && !judges.some((j) => j.id === value) && (
                                <MenuItem value={value}>
                                  <Tooltip title={value}>
                                    <span>{judgeLabel(value)}</span>
                                  </Tooltip>
                                </MenuItem>
                              )}
                              {judges.map((j) => (
                                <MenuItem key={j.id} value={j.id}>
                                  {judgeLabel(j.id)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="overline" color="text.secondary">
                {t("ring.classConfigModeHeading")}
              </Typography>
              <Box sx={{ mt: 1, mb: 2 }}>
                {/* #1783: human-set, class-level walk/trot-only override
                    (DR133.1.a permits offering a class limited to medium walk
                    and working trot; DR133.1.k requires it use the same DSE
                    scoresheet, so this can't be a test-def field or inferred
                    from the FVD class name, see score-common's
                    classSchema.walkTrotOnly comment). Gated to equitation
                    classes (EQUITATION_SECTION_CODE above); scribe-fe's
                    ClassGrid reads this to hide the Canter columns on the
                    sheet. */}
                {c.sectionCode === EQUITATION_SECTION_CODE && (
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      sx={{ display: "block" }}
                      control={
                        <Switch
                          checked={c.walkTrotOnly === true}
                          disabled={saving}
                          data-testid={`walk-trot-only-${c.id}`}
                          inputProps={{ "aria-label": t("ring.walkTrotOnly") }}
                          onChange={(e) => onSaveWalkTrotOnly(c.id, e.target.checked)}
                        />
                      }
                      label={t("ring.walkTrotOnly")}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", ml: 1.5 }}
                    >
                      {t("ring.walkTrotOnlyHelp")}
                    </Typography>
                  </Box>
                )}

                {/* #2415 (paper-scoring workstream) — human-set, class-level
                    flag: this class is judged on PAPER by the judge,
                    physically carried to the scoring tent, and keyed in by
                    show staff. Scribes are still assigned but never sign in.
                    See score-api's setClassPaperEntry (class-config.js) for
                    the numericOnly + noSignatureNeeded stamp this drives on
                    every score sheet created AFTER the flag is set — never
                    retroactively, hence the hasExistingScoreSheets warning
                    below. Gated on assign_scribes, the same permission the
                    API route requires. */}
                <Box>
                  <FormControlLabel
                    sx={{ display: "block" }}
                    control={
                      <Switch
                        checked={c.paperEntry === true}
                        disabled={saving || !canAssignScribes}
                        data-testid={`paper-entry-${c.id}`}
                        inputProps={{ "aria-label": t("ring.paperEntry") }}
                        onChange={(e) => onSavePaperEntry(c.id, e.target.checked)}
                      />
                    }
                    label={t("ring.paperEntry")}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", ml: 1.5 }}
                  >
                    {t("ring.paperEntryHelp")}
                  </Typography>
                  {hasExistingScoreSheets && (
                    <Alert
                      severity="warning"
                      sx={{ mt: 1 }}
                      data-testid={`paper-entry-existing-sheets-warning-${c.id}`}
                    >
                      {t("ring.paperEntryExistingSheetsWarning")}
                    </Alert>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="overline" color="text.secondary">
                {t("ring.classConfigResultsHeading")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                {/* #2277 — human-set, per-class DSHB kind override. Wins over
                    score-common's classifyDshbClassKind className/fvdTestId
                    guess wherever a class's DSHB kind is resolved (score-api's
                    results/championship/daysheet/scoresheet handlers,
                    scribe-fe's grid/handler-label logic, and
                    resolveClassTypeForSuggestion above) - see
                    class-config.js's setClassDshbKindOverride for why this is
                    the full enum, not a boolean. Shown for every class, not
                    gated like walk-trot-only above: any class name can defeat
                    the heuristic, not just equitation ones (the real case
                    this shipped for, WEC's '4 Y/O Sport Horse
                    Prospect/Nutrena/USDFBC Qual.', is a breed-show in-hand
                    class). Selecting "Auto-detect" sends `null`, clearing the
                    override and restoring the guess. */}
                <FormControl
                  size="small"
                  sx={{ width: 280 }}
                  data-testid={`dshb-kind-override-${c.id}`}
                >
                  <InputLabel id={`${c.id}-dshb-kind-override-label`}>
                    {t("ring.dshbKindOverride")}
                  </InputLabel>
                  <Select
                    labelId={`${c.id}-dshb-kind-override-label`}
                    label={t("ring.dshbKindOverride")}
                    value={c.dshbClassKindOverride ?? DSHB_KIND_OVERRIDE_AUTO}
                    disabled={saving}
                    MenuProps={menuProps}
                    IconComponent={saving ? SavingIcon : undefined}
                    onChange={(e) =>
                      onSaveDshbKindOverride(
                        c.id,
                        e.target.value === DSHB_KIND_OVERRIDE_AUTO ? null : e.target.value
                      )
                    }
                  >
                    <MenuItem value={DSHB_KIND_OVERRIDE_AUTO}>{t("ring.dshbKindOverrideAuto")}</MenuItem>
                    {Object.values(DSHB_CLASS_KINDS).map((kind) => (
                      <MenuItem key={kind} value={kind}>
                        {t(DSHB_KIND_OVERRIDE_LABEL_KEYS[kind])}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {c.dshbClassKindOverride === DSHB_CLASS_KINDS.NOT_DSHB
                      ? t("ring.dshbKindOverrideNotDshbHelp")
                      : t("ring.dshbKindOverrideHelp")}
                  </FormHelperText>
                </FormControl>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="overline" color="text.secondary">
                {t("ring.classConfigSponsorshipHeading")}
              </Typography>
              {/* #2567 (class-sponsorship workstream, #2565-2570) — human-set,
                  per-class sponsor (from the org's sponsor library, #1635/
                  #2566) and free-text prize. FVD carries neither. Gated on
                  assign_scribes, same permission score-api's
                  setClassSponsorship route requires. */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                <FormControl
                  size="small"
                  sx={{ width: 280 }}
                  data-testid={`sponsor-${c.id}`}
                >
                  <InputLabel id={`${c.id}-sponsor-label`}>{t("ring.sponsor")}</InputLabel>
                  <Select
                    labelId={`${c.id}-sponsor-label`}
                    label={t("ring.sponsor")}
                    value={sponsorIdInput}
                    displayEmpty
                    disabled={saving || !canAssignScribes}
                    MenuProps={menuProps}
                    IconComponent={saving ? SavingIcon : undefined}
                    onChange={handleSponsorChange}
                    // #2567 rework — screenshot-caught real defect: with no
                    // renderValue, MUI's Select default reuses the matching
                    // MenuItem's own CHILDREN (Tooltip included) to render
                    // the CLOSED field's displayed value too, not just the
                    // open listbox row. So the dangling-sponsor state put a
                    // second, independent Tooltip instance directly under
                    // the mouse in the closed field, and a real hover/click
                    // there opened it with its default enterDelay. That
                    // popper then rendered at its default "bottom" of the
                    // closed field, which is exactly where the listbox's
                    // first item lands once the menu opens, so it visually
                    // overlapped that item. renderValue here supplies a
                    // plain-text closed-state label with no Tooltip, so the
                    // raw-id-on-hover affordance stays only on the actual
                    // open listbox row, where it can't collide with
                    // anything.
                    renderValue={(val) => {
                      if (val === SPONSOR_NONE) {
                        return <em>{t("ring.sponsorNone")}</em>;
                      }
                      if (sponsorMissing && val === sponsorIdInput) {
                        return t("ring.sponsorMissing");
                      }
                      return sponsorList.find((s) => s.id === val)?.name ?? val;
                    }}
                  >
                    <MenuItem value={SPONSOR_NONE}>
                      <em>{t("ring.sponsorNone")}</em>
                    </MenuItem>
                    {sponsorMissing && (
                      <MenuItem value={sponsorIdInput}>
                        <Tooltip title={sponsorIdInput}>
                          <span>{t("ring.sponsorMissing")}</span>
                        </Tooltip>
                      </MenuItem>
                    )}
                    {sponsorList.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {sponsorMissing && (
                    <FormHelperText>{t("ring.sponsorMissingHelp")}</FormHelperText>
                  )}
                </FormControl>
                <TextField
                  label={t("ring.prize")}
                  helperText={t("ring.prizeHelp")}
                  size="small"
                  sx={{ width: 280 }}
                  data-testid={`prize-${c.id}`}
                  value={prizeInput}
                  disabled={saving || !canAssignScribes}
                  onChange={(e) => setPrizeInput(e.target.value)}
                  onBlur={handlePrizeBlur}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        <Divider sx={{ mb: 2 }} />

        <Typography variant="h6" gutterBottom color="text.primary">
          {t("ring.scribeHeading")}
        </Typography>
        {c && positions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("ring.noJudgedPositions")}
          </Typography>
        )}
        <Stack spacing={2} sx={{ mb: 2 }}>
          {c &&
            positions.map((position) => {
              const ids = c.scribeAssignments?.[position];
              const value = Array.isArray(ids) && ids.length ? ids[0] : "";
              return (
                <FormControl key={position} size="small" fullWidth>
                  <InputLabel id={`${c.id}-${position}-label`}>
                    {t("ring.positionScribe", { position })}
                  </InputLabel>
                  <Select
                    labelId={`${c.id}-${position}-label`}
                    label={t("ring.positionScribe", { position })}
                    value={value}
                    disabled={saving}
                    MenuProps={menuProps}
                    IconComponent={saving ? SavingIcon : undefined}
                    onChange={(e) => onAssign(c.id, position, e.target.value)}
                  >
                    <MenuItem value="">
                      <em>{t("ring.none")}</em>
                    </MenuItem>
                    {/* #865 — a stale/removed scribe assignment (id not in the
                        current roster) is a data problem to surface quietly;
                        the raw id — genuinely useful for support diagnosing a
                        stale assignment — goes in a tooltip, not the option
                        text. MenuItem must stay Select's direct child, so the
                        Tooltip wraps only the label inside it. */}
                    {value && !scribes.some((s) => s.userId === value) && (
                      <MenuItem value={value}>
                        <Tooltip title={value}>
                          <span>{scribeLabel(value)}</span>
                        </Tooltip>
                      </MenuItem>
                    )}
                    {scribes.map((m) => (
                      <MenuItem key={m.userId} value={m.userId}>
                        {userDisplayName(m)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            })}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* #615 — per-ride list with live status badges. #633 — moved to the
            bottom of the drawer, below the class settings. */}
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("ring.ridesHeading")}
        </Typography>
        <ClassRideList classId={c?.id} open={open} classRing={c?.ring} />
      </Box>
    </Drawer>
  );
}
